import { useEffect, useMemo, useState } from 'react';
import {
  doc, onSnapshot, collection, query, where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../context/AuthContext';
import { useUI } from '../context/UIContext';
import { subscribeConfig } from '../data/listeners/config';
import { subscribeLeadAudit } from '../data/listeners/audit';
import { updateLead, setLeadArchived, adminDeleteLead } from '../data/commands/leadCommands';
import { useAssignableAgents } from '../hooks/useAssignableAgents';
import { useTerritories } from '../context/TerritoriesContext';
import Notes from '../views/leads/Notes';
import Icon from './Icon';
import {
  STAGES, STAGE_COLOR, STAGE_BG, CADENCE_STEPS, HEALTH_COLOR, CADENCES, HEALTH,
  REQUEST_STATUS_LABEL, REQUEST_STATUS_COLOR, COVERAGE_DAYS, normalizeStage, leadValueOf,
} from '../data/leadConstants';

const toDateInput = (ms) => (ms ? new Date(ms).toISOString().slice(0, 10) : '');
const fmtTs = (ms) => (ms ? new Date(ms).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');

// Editable fields seeded from the lead doc; Save writes only what changed (audited).
const FIELDS = ['stage', 'leadScore', 'contactPerson', 'phone', 'nextAction', 'nextDate'];

export default function LeadDrawer() {
  const { leadId, closeLead, openLogVisit } = useUI();
  const { tenantId, role, profile, authUser } = useAuth();
  const agents = useAssignableAgents();
  const { territories, terrLabel } = useTerritories();
  // Reassigning the Agent (and Territory) is manager+ only (CRM revisions Task 1).
  const canReassign = role === 'manager' || role === 'owner' || role === 'hqadmin';
  // BUG-020: auditLogs is HQ/Owner/Manager-only at the rules layer; for an agent
  // the audit query is denied and the "System audit" tab renders silently empty
  // ("di gumagalaw upon touch"). Hide the tab from agents — their lead history is
  // the Notes & timeline tab (which already reflects every touch).
  const canSeeAudit = role !== 'agent';
  const [lead, setLead] = useState(null);
  const [config, setConfig] = useState(null);
  const [audit, setAudit] = useState([]);
  const [requests, setRequests] = useState([]);
  const [tab, setTab] = useState('timeline'); // 'timeline' | 'audit'
  // Seed with the field shape so inputs are controlled from first render (avoids
  // React's uncontrolled→controlled warning before the lead snapshot lands).
  const [form, setForm] = useState({
    stage: 'New', leadScore: 0, contactPerson: '', phone: '', nextAction: '', nextDate: '',
    leadValue: 0, verified: false, ownerUid: '', territoryId: '', coverageDays: [],
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  // Permanent delete is Owner/HQ-only (Jorwin 2026-07-21); everyone else archives.
  // Two-step confirm lives in-drawer; the actual delete is a gated server callable.
  const canDelete = role === 'owner' || role === 'hqadmin';
  const [confirmDelete, setConfirmDelete] = useState(false);
  const actor = { uid: authUser?.uid, name: profile?.name ?? profile?.email, role };

  useEffect(() => {
    if (!leadId) { setLead(null); return undefined; }
    setMsg(''); setTab('timeline'); setConfirmDelete(false);
    return onSnapshot(doc(db, 'leads', leadId), (snap) => {
      setLead(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    }, (e) => setMsg(e.message || 'Could not load this lead.'));
  }, [leadId]);

  useEffect(() => { if (tenantId) return subscribeConfig(tenantId, setConfig); return undefined; }, [tenantId]);
  useEffect(() => {
    if (!canSeeAudit) { setAudit([]); return undefined; } // agents can't read auditLogs (rules)
    return subscribeLeadAudit(tenantId, leadId, setAudit);
  }, [tenantId, leadId, canSeeAudit]);

  // This lead's requests (account history). Single-field leadId filter → no index.
  useEffect(() => {
    if (!leadId) { setRequests([]); return undefined; }
    const q = query(collection(db, 'requests'), where('leadId', '==', leadId));
    return onSnapshot(q, (snap) => setRequests(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), () => setRequests([]));
  }, [leadId]);

  // Seed the editable form whenever a different lead loads.
  useEffect(() => {
    if (!lead) return;
    setForm({
      stage: normalizeStage(lead.stage ?? 'New'),
      leadScore: lead.leadScore ?? 0,
      contactPerson: lead.contactPerson ?? '',
      phone: lead.phone ?? '',
      nextAction: lead.nextAction ?? '',
      nextDate: toDateInput(lead.nextDate),
      leadValue: leadValueOf(lead),
      verified: !!lead.verified,
      ownerUid: lead.ownerUid ?? '',
      territoryId: lead.territoryId ?? '',
      coverageDays: Array.isArray(lead.coverageDays) ? lead.coverageDays : [],
    });
  }, [lead?.id]);

  const dirtyPatch = useMemo(() => {
    if (!lead) return {};
    const patch = {};
    if (form.stage !== normalizeStage(lead.stage ?? 'New')) patch.stage = form.stage;
    if (Number(form.leadScore) !== (lead.leadScore ?? 0)) patch.leadScore = Number(form.leadScore) || 0;
    if ((form.contactPerson ?? '') !== (lead.contactPerson ?? '')) patch.contactPerson = form.contactPerson;
    if ((form.phone ?? '') !== (lead.phone ?? '')) patch.phone = form.phone;
    if ((form.nextAction ?? '') !== (lead.nextAction ?? '')) patch.nextAction = form.nextAction;
    const formMs = form.nextDate ? new Date(form.nextDate).getTime() : null;
    if (formMs !== (lead.nextDate ?? null)) patch.nextDate = formMs;
    // Lead value (Task 4) — compared against the displayed value so legacy leads
    // aren't falsely dirty; saving heals them onto `leadValue` going forward.
    if (Number(form.leadValue) !== leadValueOf(lead)) patch.leadValue = Number(form.leadValue) || 0;
    // Verified sub-status (Task 2, New leads only).
    if (!!form.verified !== !!lead.verified) patch.verified = !!form.verified;
    // Agent reassignment (Task 1, manager+). Carry the resolved name too.
    if ((form.ownerUid ?? '') !== (lead.ownerUid ?? '')) {
      patch.ownerUid = form.ownerUid || null;
      patch.ownerName = agents.find((a) => a.uid === form.ownerUid)?.name ?? null;
    }
    // Territory reassignment (Task 1, manager+).
    if ((form.territoryId ?? '') !== (lead.territoryId ?? '')) patch.territoryId = form.territoryId || null;
    // Coverage days (Task 3) — order-insensitive compare.
    const a = [...(form.coverageDays || [])].sort().join(',');
    const b = [...(Array.isArray(lead.coverageDays) ? lead.coverageDays : [])].sort().join(',');
    if (a !== b) patch.coverageDays = form.coverageDays || [];
    // Won-customer card fields (only present once edited).
    if (form.health !== undefined && form.health !== (lead.health ?? 'Healthy')) patch.health = form.health;
    if (form.serviceCadence !== undefined && form.serviceCadence !== (lead.serviceCadence ?? 'Weekly')) patch.serviceCadence = form.serviceCadence;
    return patch;
  }, [form, lead, agents]);

  const dirty = Object.keys(dirtyPatch).length > 0;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleDay = (day) => setForm((f) => {
    const has = (f.coverageDays || []).includes(day);
    return { ...f, coverageDays: has ? f.coverageDays.filter((d) => d !== day) : [...(f.coverageDays || []), day] };
  });

  async function save() {
    setSaving(true); setMsg('');
    try {
      await updateLead(lead.id, dirtyPatch, actor);
      setMsg('Saved.');
    } catch (e) {
      setMsg(e.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  async function toggleArchive() {
    setSaving(true); setMsg('');
    try {
      await setLeadArchived(lead.id, !lead.archived, actor);
      setMsg(lead.archived ? 'Un-archived.' : 'Archived.');
    } catch (e) {
      setMsg(e.message || 'Could not change archive status.');
    } finally {
      setSaving(false);
    }
  }

  async function doDelete() {
    setSaving(true); setMsg('');
    try {
      await adminDeleteLead(lead.id);
      closeLead(); // record + notes are gone; audit keeps the full snapshot
    } catch (e) {
      setMsg(e.message || 'Could not delete.');
      setSaving(false);
    }
  }

  if (!leadId) return null;
  const isCustomer = lead?.lifecycle === 'customer';
  const cadenceStep = lead?.cadenceStep ?? 0;
  const stageNow = normalizeStage(form.stage);
  const isClosed = stageNow === 'Won' || stageNow === 'Lost';

  return (
    <>
      <div className="scrim" onClick={closeLead} />
      <aside className="drawer">
        <header>
          <div>
            <div className="muted" style={{ fontSize: 'var(--text-caption)' }}>
              {lead?.id}{lead?.territoryId ? ` · ${terrLabel(lead.territoryId)}` : ''}
              {/* v1.1 source tracing: origin shown for chatbot leads only ("Source", never "Channel"). */}
              {lead?.source === 'inbound-messenger' && (
                ` · via ${({ facebook: 'Facebook', instagram: 'Instagram', tiktok: 'TikTok' })[lead.sourcePlatform] ?? 'Facebook'} chatbot${({ en: ' · English', fil: ' · Filipino', 'zh-TW': ' · 中文(繁)', 'zh-CN': ' · 中文(简)' })[lead.language] ?? ''}`
              )}
            </div>
            <h2>{lead?.businessName ?? 'Lead'}</h2>
            {lead && (
              <div className="row" style={{ marginTop: 8 }}>
                <span className="badge" style={{ background: STAGE_BG[normalizeStage(lead.stage)], color: STAGE_COLOR[normalizeStage(lead.stage)] }}>
                  <span className="dot" style={{ background: STAGE_COLOR[normalizeStage(lead.stage)] }} />{normalizeStage(lead.stage)}
                </span>
                {lead.archived && <span className="badge" style={{ background: 'var(--presto-neutral-100)', color: 'var(--color-fg-muted)' }}>Archived</span>}
                {lead.tier && <span className={`badge tier-${lead.tier}`}>{lead.tier}</span>}
                {lead.category && <span className="badge tier-Mid">{lead.category}</span>}
              </div>
            )}
          </div>
          <button className="x" onClick={closeLead} aria-label="Close">✕</button>
        </header>

        <div className="body">
          {!lead ? (
            <div className="muted">{msg || 'Loading…'}</div>
          ) : (
            <>
              <button className="btn btn-primary" style={{ marginBottom: 'var(--space-4)' }} onClick={() => openLogVisit(lead.id)}>
                <Icon name="logvisit" size={15} /> Log Touchpoint
              </button>

              {isCustomer && (
                <div className="card card-pad" style={{ background: 'var(--presto-success-bg)', borderColor: '#bfe3cf', marginBottom: 'var(--space-4)' }}>
                  <div className="row" style={{ marginBottom: 12 }}>
                    <Icon name="visits" size={18} /><b>Active customer — visits in progress</b>
                    <span className="badge" style={{ background: '#fff', color: HEALTH_COLOR[lead.health ?? 'Healthy'], marginLeft: 'auto' }}>
                      <span className="dot" style={{ background: HEALTH_COLOR[lead.health ?? 'Healthy'] }} />{lead.health ?? 'Healthy'}
                    </span>
                  </div>
                  <div className="grid2">
                    <div className="field"><label>Account health</label><select className="input" value={form.health ?? lead.health ?? 'Healthy'} onChange={set('health')}>{HEALTH.map((h) => <option key={h}>{h}</option>)}</select></div>
                    <div className="field"><label>Visit cadence</label><select className="input" value={form.serviceCadence ?? lead.serviceCadence ?? 'Weekly'} onChange={set('serviceCadence')}>{CADENCES.map((c) => <option key={c}>{c}</option>)}</select></div>
                  </div>
                </div>
              )}

              <div className="grid2">
                <div className="field"><label>Stage</label>
                  <select className="input" value={form.stage} onChange={set('stage')}>{STAGES.map((s) => <option key={s}>{s}</option>)}</select>
                </div>
                <div className="field"><label>Lead score</label><input className="input" value={form.leadScore} onChange={set('leadScore')} /></div>
                <div className="field"><label>Lead value amount (₱/mo)</label><input className="input" type="number" min="0" value={form.leadValue} onChange={set('leadValue')} /></div>
                <div className="field"><label>Assigned agent</label>
                  {canReassign ? (
                    <select className="input" value={form.ownerUid} onChange={set('ownerUid')}>
                      <option value="">— unassigned —</option>
                      {agents.map((a) => <option key={a.uid} value={a.uid}>{a.name}</option>)}
                      {form.ownerUid && !agents.some((a) => a.uid === form.ownerUid)
                        && <option value={form.ownerUid}>{lead.ownerName ?? form.ownerUid}</option>}
                    </select>
                  ) : (
                    <input className="input" value={lead.ownerName ?? '— unassigned —'} disabled title="Only a manager can reassign the agent" />
                  )}
                </div>
                <div className="field"><label>Territory</label>
                  {canReassign ? (
                    <select className="input" value={form.territoryId} onChange={set('territoryId')}>
                      <option value="">— none —</option>
                      {territories.map((t) => <option key={t.id} value={t.id}>{t.label ?? t.id}</option>)}
                    </select>
                  ) : (
                    <input className="input" value={form.territoryId ? terrLabel(form.territoryId) : '—'} disabled />
                  )}
                </div>
                <div className="field"><label>Contact person</label><input className="input" value={form.contactPerson} onChange={set('contactPerson')} /></div>
                <div className="field"><label>Phone / Viber</label><input className="input" value={form.phone} onChange={set('phone')} /></div>
                <div className="field"><label>Next action</label><input className="input" value={form.nextAction} onChange={set('nextAction')} /></div>
                <div className="field"><label>Next date</label><input className="input" type="date" value={form.nextDate} onChange={set('nextDate')} /></div>
              </div>

              {stageNow === 'New' && (
                <label className="row" style={{ gap: 8, marginTop: 'var(--space-3)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!form.verified} onChange={(e) => setForm((f) => ({ ...f, verified: e.target.checked }))} />
                  <span>Verified <span className="muted" style={{ fontSize: 'var(--text-caption)' }}>— this new lead has been verified</span></span>
                </label>
              )}

              {isCustomer && (
                <div className="field" style={{ marginTop: 'var(--space-3)' }}>
                  <label>Coverage days</label>
                  <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                    {COVERAGE_DAYS.map((d) => {
                      const on = (form.coverageDays || []).includes(d);
                      return (
                        <span key={d} className={`chip ${on ? 'on' : ''}`} style={{ cursor: 'pointer' }} onClick={() => toggleDay(d)}>{d}</span>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="row" style={{ justifyContent: 'space-between', marginTop: 'var(--space-3)' }}>
                <span className="muted" style={{ fontSize: 'var(--text-caption)' }}>{msg}</span>
                <div className="row" style={{ gap: 8 }}>
                  {canDelete && !confirmDelete && (
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--presto-error)' }} disabled={saving} onClick={() => setConfirmDelete(true)}>
                      Delete
                    </button>
                  )}
                  {canDelete && confirmDelete && (
                    <>
                      <span className="muted" style={{ fontSize: 'var(--text-caption)' }}>Permanently delete this lead? Audited; not undoable.</span>
                      <button className="btn btn-sm" style={{ background: 'var(--presto-error)', color: '#fff' }} disabled={saving} onClick={doDelete}>
                        {saving ? 'Deleting…' : 'Delete forever'}
                      </button>
                      <button className="btn btn-ghost btn-sm" disabled={saving} onClick={() => setConfirmDelete(false)}>Cancel</button>
                    </>
                  )}
                  {(isClosed || lead.archived) && (
                    <button className="btn btn-ghost btn-sm" disabled={saving} onClick={toggleArchive}>
                      {lead.archived ? 'Un-archive' : 'Archive'}
                    </button>
                  )}
                  <button className="btn btn-primary btn-sm" disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save changes'}</button>
                </div>
              </div>

              <div className="lead-section"><Icon name="leads" size={16} /> Outreach cadence</div>
              <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                {CADENCE_STEPS.map((s, i) => {
                  const done = i < cadenceStep; const cur = i === cadenceStep;
                  return (
                    <span key={s} className="badge" style={{
                      background: done ? 'var(--presto-success-bg)' : cur ? 'var(--presto-sunny-light)' : 'var(--presto-neutral-100)',
                      color: done ? 'var(--presto-success)' : cur ? 'var(--presto-sunny-dark)' : 'var(--presto-neutral-600)',
                    }}>{done ? '✓ ' : cur ? '▸ ' : ''}{s}</span>
                  );
                })}
              </div>
              <p className="lockline">Suggested next: <b>{CADENCE_STEPS[cadenceStep] ?? '—'}</b> — guidance only, no auto-send.</p>

              {requests.length > 0 && (
                <>
                  <div className="lead-section"><Icon name="requests" size={16} /> Requests ({requests.length})</div>
                  {requests.map((r) => (
                    <div key={r.id} className="row" style={{ justifyContent: 'space-between', padding: '8px 0', borderBottom: 'var(--border-thin) dashed var(--color-border)' }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 'var(--text-small)' }}>{r.text}</div>
                        {r.remark && <div className="meta"><b>Remark:</b> {r.remark}</div>}
                      </div>
                      <span className="badge" style={{ background: `${REQUEST_STATUS_COLOR[r.status]}1a`, color: REQUEST_STATUS_COLOR[r.status], flex: 'none' }}>
                        <span className="dot" style={{ background: REQUEST_STATUS_COLOR[r.status] }} />{REQUEST_STATUS_LABEL[r.status] ?? r.status}
                      </span>
                    </div>
                  ))}
                </>
              )}

              <div className="tabs">
                <div className={`tab ${tab === 'timeline' ? 'active' : ''}`} onClick={() => setTab('timeline')}>Notes &amp; timeline (sales journey)</div>
                {canSeeAudit && (
                  <div className={`tab ${tab === 'audit' ? 'active' : ''}`} onClick={() => setTab('audit')}>System audit</div>
                )}
              </div>

              {(tab !== 'audit' || !canSeeAudit) ? (
                <Notes leadId={lead.id} config={config} actor={actor} />
              ) : (
                <>
                  <table>
                    <thead><tr><th>When</th><th>Who</th><th>Field</th><th>Change</th></tr></thead>
                    <tbody>
                      {audit.map((a) => (
                        <tr key={a.id} className="audit-row">
                          <td>{fmtTs(a.ts)}</td>
                          <td>{a.displayName ?? a.uid ?? 'system'}</td>
                          <td>{a.field}</td>
                          <td><span className="diff-old">{String(a.oldValue ?? '—')}</span> → <span className="diff-new">{String(a.newValue ?? '—')}</span></td>
                        </tr>
                      ))}
                      {audit.length === 0 && <tr><td colSpan={4} className="muted">No system changes yet.</td></tr>}
                    </tbody>
                  </table>
                  <p className="lockline">Append-only, server-written — cannot be edited or deleted by anyone.</p>
                </>
              )}
            </>
          )}
        </div>
      </aside>
    </>
  );
}
